import { useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import {
  Activity,
  Calendar,
  ChevronRight,
  Eye,
  ExternalLink,
  Handshake,
  Inbox,
  Loader2,
  Mail,
  MoreVertical,
  Plus,
  RefreshCw,
  StickyNote,
  Trash2,
  TrendingUp,
  UserCog,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { cn, formatCurrencyEUR, formatDate, formatRelative } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/shared/empty-state";
import { PartnerTypeBadge } from "@/components/shared/partner-type-badge";
import { PartnerStatusBadge } from "@/components/shared/partner-status-badge";
import { PartnerTierAssignmentPopover } from "./tier-assignment-popover";
import { HubspotLink } from "@/components/shared/hubspot-link";
import { usePartner } from "@/features/partners/use-partner";
import { usePartnerUsers } from "@/features/partners/use-partner-users";
import { useOnboardingTasks } from "@/features/partners/use-onboarding-tasks";
import { useSyncPartner } from "@/features/partners/use-sync-partner";
import { useUpdatePartnerNotes } from "@/features/partners/use-update-partner-notes";
import { useAddOnboardingTask } from "@/features/partners/use-add-onboarding-task";
import { useToggleOnboardingTask } from "@/features/partners/use-toggle-onboarding-task";
import { useDeleteOnboardingTask } from "@/features/partners/use-delete-onboarding-task";
import { useInvitePartnerContact } from "@/features/partners/use-invite-partner-contact";
import { useRemovePartnerUser } from "@/features/partners/use-remove-partner-user";
import { useUpdatePartnerUserRole } from "@/features/partners/use-update-partner-user-role";
import { useTogglePartnerUserPrimary } from "@/features/partners/use-toggle-partner-user-primary";
import { useResendPartnerInvite } from "@/features/partners/use-resend-partner-invite";
import { useRevokePartnerInvite } from "@/features/partners/use-revoke-partner-invite";
import { useDeletePartner } from "@/features/partners/use-delete-partner";
import {
  useAdminPartnerOverview,
  type AdminPartnerOverviewDeal,
  type AdminPartnerOverviewTask,
} from "./use-admin-partner-overview";
import {
  useAdminPartnerDeals,
  type AdminPartnerDealRow,
  type AdminPartnerDealStatus,
} from "./use-admin-partner-deals";
import { DealDrawer } from "../deals/deal-drawer";
import { usePreviewAs } from "@/lib/preview-as-context";
import { ApiClientError } from "@/lib/api-client";
import { PartnerHubspotExtrasPanels } from "./partner-hubspot-extras-panels";
import { IntegrationsTab } from "./integrations-tab";
import { MarketplaceListingCard } from "./marketplace-listing-card";

// Phase 7 sub-bucket 10 — admin partner detail.
//
// 4 tabs in this port: overview / people / onboarding / notes. The legacy
// page also exposed analytics, deals, content, goals, tasks (alias for
// onboarding) and a "Apply experience" journey-assignment button. Those are
// deferred to follow-up sub-buckets since they each depend on either:
//   - admin analytics endpoints (the portal analytics ones gate on
//     session.partnerId; admin needs a partnerId-override surface);
//   - the deals / content / goals admin pages landing first.
// The sub-bucket-6 hoist of `<PartnerAnalyticsView>` is still load-bearing
// for the follow-up.
//
// Edit-partner field-level autosave is also deferred — legacy never wired
// the pencil-button in PartnerDetailDrawer to a real form, and adding one
// here would exceed the strict-parity scope. Tier assignment, content view
// counts, and goal CRUD are all separate workstreams (sub-buckets 11+).
type Tab = "overview" | "people" | "deals" | "tasks" | "notes" | "knowledge-base";
const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "people", label: "People" },
  { key: "deals", label: "Deals" },
  { key: "tasks", label: "Tasks" },
  { key: "notes", label: "Notes" },
  // Internal-only Notion Knowledge Base. Safe to surface for all partners —
  // the tab self-describes its empty state and linking is explicit.
  { key: "knowledge-base", label: "Knowledge Base" },
];

function normalizeTab(raw: string | null): Tab {
  if (
    raw === "overview" ||
    raw === "people" ||
    raw === "deals" ||
    raw === "tasks" ||
    raw === "notes" ||
    raw === "knowledge-base"
  ) {
    return raw;
  }
  // "onboarding" alias kept for old links.
  if (raw === "onboarding") return "tasks";
  // "integrations" alias kept for old links (tab renamed to Knowledge Base).
  if (raw === "integrations") return "knowledge-base";
  return "overview";
}

// Opens the shared admin deal drawer on this page via the `?deal=` URL param —
// the same contract the kanban board and admin dashboard use. Pass null to
// close. Any deal-row body click routes here; only the ↗ icon escapes to
// HubSpot.
function useDealDrawerParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDealId = searchParams.get("deal");
  const setDeal = (hubspotDealId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (hubspotDealId) {
      next.set("deal", hubspotDealId);
    } else {
      next.delete("deal");
    }
    setSearchParams(next, { replace: true });
  };
  return { selectedDealId, setDeal };
}

export function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  if (!id) return <Navigate to="/admin/partners" replace />;

  return (
    <PartnerDetailInner
      partnerId={id}
      tab={tab}
      onTabChange={(next) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", next);
        setSearchParams(sp, { replace: true });
      }}
    />
  );
}

function PartnerDetailInner({
  partnerId,
  tab,
  onTabChange,
}: {
  partnerId: string;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const partnerQuery = usePartner(partnerId);
  const overviewQuery = useAdminPartnerOverview(partnerId);
  // Must run before any early return — hooks can't be called conditionally
  // (React error #310 otherwise).
  const { selectedDealId, setDeal } = useDealDrawerParam();

  if (partnerQuery.isLoading) {
    return <DetailSkeleton />;
  }
  if (partnerQuery.isError || !partnerQuery.data) {
    return (
      <EmptyState
        icon={Inbox}
        title="Couldn't load partner"
        description={
          partnerQuery.error?.code === "not_found"
            ? "This partner has been deleted or never existed."
            : (partnerQuery.error?.message ?? "Try refreshing.")
        }
        action={
          <Link
            to="/admin/partners"
            className="text-sm text-accent hover:underline"
          >
            ← Back to partners
          </Link>
        }
      />
    );
  }
  const partner = partnerQuery.data;
  const overview = overviewQuery.data ?? null;
  const counts = overview?.counts ?? {
    people: 0,
    deals: 0,
    dealsAttributed: 0,
    openTasks: 0,
  };

  return (
    <div className="space-y-6">
      <PartnerHeader
        partner={partner}
        hubspotPartnerUrl={overview?.hubspotPartnerUrl ?? null}
      />

      <TabsNav active={tab} counts={counts} onChange={onTabChange} />

      <div role="tabpanel">
        {tab === "overview" && (
          <OverviewTab
            overview={overview}
            loading={overviewQuery.isLoading}
            partnerId={partnerId}
            marketplaceIntegrationId={partner.marketplace_integration_id}
          />
        )}
        {tab === "people" && <PeopleTab partnerId={partnerId} />}
        {tab === "deals" && <DealsTab partnerId={partnerId} />}
        {tab === "tasks" && <TasksTab partnerId={partnerId} />}
        {tab === "notes" && <NotesTab partnerId={partnerId} initial={partner.notes ?? ""} />}
        {tab === "knowledge-base" && <IntegrationsTab partnerId={partnerId} />}
      </div>

      <DealDrawer hubspotDealId={selectedDealId} onClose={() => setDeal(null)} />
    </div>
  );
}

// ============================================================================
// Header
// ============================================================================

function PartnerHeader({
  partner,
  hubspotPartnerUrl,
}: {
  partner: ReturnType<typeof usePartner>["data"] & {};
  hubspotPartnerUrl: string | null;
}) {
  const navigate = useNavigate();
  const sync = useSyncPartner();
  const deletePartner = useDeletePartner();
  const { enter: enterPreviewAs } = usePreviewAs();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  async function handleDelete() {
    await deletePartner.mutateAsync(partner.id);
    toast.success("Partner deleted");
    navigate("/admin/partners");
  }

  async function handleSync() {
    const result = await sync.mutateAsync(partner.id);
    toast.success(
      result.created ? "HubSpot record created" : "Synced with HubSpot",
    );
  }

  async function handlePreviewAs() {
    setPreviewing(true);
    // Capture the source URL before the navigation so the banner's "Back to
    // admin" can route back here instead of falling through to the generic
    // /admin/dashboard. Must come before navigate(...) since the portal
    // routes would otherwise overwrite location.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    try {
      await enterPreviewAs(partner.id, { returnTo });
      // Same-tab navigation — sessionStorage is per-tab, so window.open
      // would land on an empty store and ProtectedRoute would bounce back
      // to /admin/dashboard.
      navigate("/portal");
    } catch (err) {
      const code = err instanceof ApiClientError ? err.code : null;
      if (code === "preview_as_internal_only") {
        toast.error("Only internal users can preview as a partner");
      } else if (code === "partner_not_found") {
        toast.error("Partner not in HubSpot mirror");
      } else {
        toast.error("Couldn't start preview. Try again.");
      }
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          <Link to="/admin/partners" className="hover:text-foreground">
            Partners
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{partner.company_name}</span>
        </nav>
        <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight">
          {partner.company_name}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <PartnerTypeBadge type={partner.partner_type as never} />
          {/* Native tier assignment popover. Assignment-driven (0085): shows
              the assigned default-program tier, else "Unassigned". */}
          <PartnerTierAssignmentPopover partnerId={partner.id} />
          <PartnerStatusBadge status={partner.status as never} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {hubspotPartnerUrl && (
          <HubspotLink href={hubspotPartnerUrl} label="View in HubSpot" />
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={sync.isPending}
        >
          {sync.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          )}
          Sync HubSpot
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setInviteOpen(true)}
        >
          <UserPlus className="mr-1.5 h-3.5 w-3.5" />
          Invite contact
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePreviewAs}
          disabled={previewing}
          title="View this partner's portal as if you were them. Use the banner's 'Back to admin' to return here."
        >
          {previewing ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Eye className="mr-1.5 h-3.5 w-3.5" />
          )}
          Preview as partner
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">More actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-xs text-muted-foreground"
              disabled
            >
              Created {formatDate(partner.created_at)}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Delete partner
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this partner?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {partner.company_name} will be removed along with all
                    associated contacts and onboarding tasks. HubSpot records
                    are not affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
        <InvitePartnerDialog
          partnerId={partner.id}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Tabs nav
// ============================================================================

function TabsNav({
  active,
  counts,
  onChange,
}: {
  active: Tab;
  counts: {
    people: number;
    deals: number;
    dealsAttributed: number;
    openTasks: number;
  };
  onChange: (next: Tab) => void;
}) {
  return (
    <nav className="flex flex-wrap items-center gap-1 border-b border-border/70">
      {TABS.map((t) => {
        const isActive = active === t.key;
        const count =
          t.key === "people"
            ? counts.people
            : t.key === "deals"
              ? counts.dealsAttributed
              : t.key === "tasks"
                ? counts.openTasks
                : null;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cn(
              "relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {count !== null && count > 0 && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                  isActive
                    ? "bg-accent/15 text-accent"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {count}
              </span>
            )}
            {isActive && (
              <span
                aria-hidden
                className="absolute inset-x-0 -bottom-px h-0.5 bg-accent"
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ============================================================================
// Overview tab
// ============================================================================

function OverviewTab({
  overview,
  loading,
  partnerId,
  marketplaceIntegrationId,
}: {
  overview: ReturnType<typeof useAdminPartnerOverview>["data"] | null;
  loading: boolean;
  partnerId: string;
  marketplaceIntegrationId: number | null;
}) {
  if (loading || !overview) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl lg:col-span-2" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <RevenueCard
          total={overview.revenueEur}
          wonCount={overview.wonCount}
          introCount={overview.introCount}
        />
        <EngagementCard />
        <WonDealsCard items={overview.wonDeals} className="lg:col-span-2" />
        <TasksOverviewCard items={overview.openTasks} />
        <NotesPreviewCard />
        {/* 0091 — marketplace listing link (activates the portal "My
            Listing" self-service surface for this partner). */}
        <MarketplaceListingCard
          partnerId={partnerId}
          marketplaceIntegrationId={marketplaceIntegrationId}
        />
        <ActivityCard />
      </div>
      <PartnerHubspotExtrasPanels
        extras={overview.hubspot_extras ?? {}}
        map={overview.hubspot_field_map ?? []}
      />
    </div>
  );
}

function CardShell({
  title,
  icon: Icon,
  action,
  className,
  children,
}: {
  title: string;
  icon: LucideIcon;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("rounded-xl border bg-card", className)}>
      <header className="flex items-center justify-between border-b border-border/60 px-5 py-3">
        <h3 className="inline-flex items-center gap-2 font-display text-sm font-semibold tracking-tight">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {title}
        </h3>
        {action}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
      {children}
    </p>
  );
}

function RevenueCard({
  total,
  wonCount,
  introCount,
}: {
  total: number;
  wonCount: number;
  introCount: number;
}) {
  return (
    <CardShell title="Revenue" icon={TrendingUp}>
      <div className="flex items-end gap-2">
        <span className="font-display text-3xl font-semibold tabular-nums tracking-tight">
          {formatCurrencyEUR(total)}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>Closed-won attributed to this partner</span>
        <span>{wonCount} {wonCount === 1 ? "deal" : "deals"}</span>
      </div>
      <p
        className="mt-2 text-[11px] text-muted-foreground"
        title="Deals Bookboost referred to this partner or co-influenced toward them — not counted in the revenue total."
      >
        Bookboost intros:{" "}
        <span className="tabular-nums text-foreground">{introCount}</span>{" "}
        {introCount === 1 ? "deal" : "deals"} sent
      </p>
    </CardShell>
  );
}

function EngagementCard() {
  return (
    <CardShell title="Engagement" icon={Activity}>
      <div className="flex items-end gap-2">
        <span className="font-display text-3xl font-semibold tabular-nums tracking-tight">
          0
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Activity score coming soon — will roll up logins, deal updates and tasks.
      </p>
    </CardShell>
  );
}

function WonDealsCard({
  items,
  className,
}: {
  items: AdminPartnerOverviewDeal[];
  className?: string;
}) {
  const { setDeal } = useDealDrawerParam();
  return (
    <CardShell title="Won deals" icon={TrendingUp} className={className}>
      {items.length === 0 ? (
        <EmptyRow>No won deals yet</EmptyRow>
      ) : (
        <ul className="divide-y divide-border/60">
          {items.map((d) => (
            <li
              key={d.hubspotDealId}
              className="group flex items-center justify-between gap-3 py-2 text-sm"
            >
              <button
                type="button"
                onClick={() => setDeal(d.hubspotDealId)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-full bg-status-active"
                />
                <span className="truncate font-medium group-hover:text-accent">
                  {d.title}
                </span>
              </button>
              <span className="shrink-0 tabular-nums text-foreground">
                {d.currency === "EUR"
                  ? formatCurrencyEUR(d.amount)
                  : `${d.amount?.toLocaleString() ?? "—"} ${d.currency}`}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatRelative(d.wonAt)}
              </span>
              <a
                href={d.hubspotUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-accent"
                title="Open in HubSpot"
                aria-label="Open in HubSpot"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function TasksOverviewCard({ items }: { items: AdminPartnerOverviewTask[] }) {
  return (
    <CardShell title="Tasks" icon={Calendar}>
      {items.length === 0 ? (
        <EmptyRow>No open tasks</EmptyRow>
      ) : (
        <ul className="divide-y divide-border/60">
          {items.slice(0, 3).map((t) => (
            <li
              key={t.id}
              className="flex items-start justify-between gap-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{t.title}</p>
                <p className="text-xs text-muted-foreground">
                  {t.assigned_to_partner ? "Partner-assigned" : "Internal"}
                </p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {t.due_date ? formatDate(t.due_date) : "No due date"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function NotesPreviewCard() {
  return (
    <CardShell title="Notes" icon={StickyNote}>
      <EmptyRow>Open the Notes tab to read and edit</EmptyRow>
    </CardShell>
  );
}

function ActivityCard({ className }: { className?: string }) {
  return (
    <CardShell title="Activity" icon={Handshake} className={className}>
      <EmptyRow>Activity feed coming soon</EmptyRow>
    </CardShell>
  );
}

// ============================================================================
// People tab
// ============================================================================

function PeopleTab({ partnerId }: { partnerId: string }) {
  const query = usePartnerUsers(partnerId);
  const remove = useRemovePartnerUser();
  const togglePrimary = useTogglePartnerUserPrimary();
  const resend = useResendPartnerInvite();
  const revoke = useRevokePartnerInvite();
  const [editing, setEditing] = useState<{ id: string; role: string } | null>(null);
  const updateRole = useUpdatePartnerUserRole();

  if (query.isLoading) {
    return <Skeleton className="h-32 rounded-xl" />;
  }
  if (query.isError) {
    return (
      <EmptyState
        icon={Users}
        title="Couldn't load contacts"
        description={query.error?.message ?? "Try refreshing."}
      />
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No contacts yet"
        description="Invite the partner's primary contact to start collaborating."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Email</th>
            <th className="px-4 py-2.5">Role</th>
            <th className="px-4 py-2.5">Added</th>
            <th className="px-4 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((u) => (
            <tr key={u.id} className="hover:bg-muted/30">
              <td className="px-4 py-3">
                <div className="font-medium">{u.name}</div>
                {u.is_primary && (
                  <span className="mt-0.5 inline-block rounded bg-accent/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-accent">
                    Primary
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                <a
                  href={`mailto:${u.email}`}
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  <Mail className="h-3 w-3" />
                  {u.email}
                </a>
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {u.role_at_partner ?? "—"}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {formatRelative(u.created_at)}
              </td>
              <td className="px-4 py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <MoreVertical className="h-3.5 w-3.5" />
                      <span className="sr-only">Actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!u.is_primary && (
                      <DropdownMenuItem
                        onClick={() =>
                          togglePrimary.mutate({
                            partnerId,
                            partnerUserId: u.id,
                            primary: true,
                          })
                        }
                      >
                        Make primary
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() =>
                        setEditing({ id: u.id, role: u.role_at_partner ?? "" })
                      }
                    >
                      <UserCog className="mr-2 h-3.5 w-3.5" />
                      Edit role
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        resend.mutate({ partnerId, partnerUserId: u.id })
                      }
                    >
                      Resend invite
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        revoke.mutate({ partnerId, partnerUserId: u.id })
                      }
                    >
                      Revoke invite
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Remove
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            They&apos;ll lose portal access immediately. Pending
                            invites for this user will also be deleted.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              remove.mutate({ partnerId, partnerUserId: u.id })
                            }
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <EditRoleDialog
        open={editing !== null}
        initialRole={editing?.role ?? ""}
        pending={updateRole.isPending}
        onCancel={() => setEditing(null)}
        onSave={async (role) => {
          if (!editing) return;
          await updateRole.mutateAsync({
            partnerId,
            partnerUserId: editing.id,
            role,
          });
          toast.success("Role updated");
          setEditing(null);
        }}
      />
    </div>
  );
}

function EditRoleDialog({
  open,
  initialRole,
  pending,
  onCancel,
  onSave,
}: {
  open: boolean;
  initialRole: string;
  pending: boolean;
  onCancel: () => void;
  onSave: (role: string) => void;
}) {
  const [role, setRole] = useState(initialRole);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit role at partner</DialogTitle>
          <DialogDescription>
            Free-text — up to 200 characters.
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          value={role}
          onChange={(e) => setRole(e.target.value)}
          maxLength={200}
          placeholder="e.g. Head of Partnerships"
          key={initialRole}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onSave(role.trim())} disabled={pending}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Deals tab — full attributed-deal list (won + open + lost)
// ============================================================================
//
// The Overview tab's WonDealsCard caps at 3 won deals; this tab is the
// source-of-truth list. Default filter chip is "Open" because pipeline
// visibility is the more actionable admin lens; Won/Lost are archive views.

type DealFilter = "all" | "open" | "won" | "lost";

function DealsTab({ partnerId }: { partnerId: string }) {
  const query = useAdminPartnerDeals(partnerId);
  const [filter, setFilter] = useState<DealFilter>("open");

  if (query.isLoading) {
    return <Skeleton className="h-64 rounded-xl" />;
  }
  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={Handshake}
        title="Couldn't load deals"
        description={query.error?.message ?? "Try refreshing."}
      />
    );
  }

  const { rows, counts } = query.data;
  const visibleRows =
    filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Every HubSpot deal attributed to this partner — open pipeline, closed
        wins, and closed losses. Click a deal to view it in Colab, or the ↗
        icon to open it in HubSpot.
      </p>

      <DealFilterChips filter={filter} counts={counts} onChange={setFilter} />

      {visibleRows.length === 0 ? (
        <EmptyRow>
          {filter === "all"
            ? "No deals attributed to this partner yet."
            : `No ${filter} deals.`}
        </EmptyRow>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/30 text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Deal</th>
                <th className="px-4 py-2 text-left font-medium">Stage</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
                <th className="px-4 py-2 text-left font-medium">Close date</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
                <th className="px-4 py-2 text-right font-medium">
                  <span className="sr-only">Open in HubSpot</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {visibleRows.map((row) => (
                <DealRow key={row.hubspotDealId} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DealFilterChips({
  filter,
  counts,
  onChange,
}: {
  filter: DealFilter;
  counts: { all: number; open: number; won: number; lost: number };
  onChange: (next: DealFilter) => void;
}) {
  const chips: Array<{ key: DealFilter; label: string; count: number }> = [
    { key: "all", label: "All", count: counts.all },
    { key: "open", label: "Open", count: counts.open },
    { key: "won", label: "Won", count: counts.won },
    { key: "lost", label: "Lost", count: counts.lost },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => {
        const active = filter === chip.key;
        return (
          <button
            key={chip.key}
            type="button"
            onClick={() => onChange(chip.key)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              active
                ? "border-accent/40 bg-accent/10 text-accent"
                : "border-border/70 bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            {chip.label}
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                active
                  ? "bg-accent/20 text-accent"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {chip.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DealStatusBadge({
  status,
  label,
}: {
  status: AdminPartnerDealStatus;
  label: string;
}) {
  const tone =
    status === "won"
      ? "bg-status-active/15 text-status-active"
      : status === "lost"
        ? "bg-destructive/10 text-destructive"
        : "bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        tone,
      )}
    >
      {label}
    </span>
  );
}

function DealRow({ row }: { row: AdminPartnerDealRow }) {
  const { setDeal } = useDealDrawerParam();
  return (
    <tr
      className="cursor-pointer text-sm transition-colors hover:bg-muted/40"
      onClick={() => setDeal(row.hubspotDealId)}
    >
      <td className="px-4 py-2.5">
        <span className="truncate font-medium text-foreground">
          {row.title}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <DealStatusBadge
          status={row.status}
          label={row.stageLabel ?? row.stageId ?? row.status}
        />
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {row.amount === null
          ? "—"
          : row.currency === "EUR"
            ? formatCurrencyEUR(row.amount)
            : `${row.amount.toLocaleString()} ${row.currency}`}
      </td>
      <td className="px-4 py-2.5 text-muted-foreground">
        {row.closeDate ? formatDate(row.closeDate) : "—"}
      </td>
      <td className="px-4 py-2.5 text-xs text-muted-foreground">
        {formatRelative(row.createdAt)}
      </td>
      <td className="px-4 py-2.5 text-right">
        <a
          href={row.hubspotUrl}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex rounded p-0.5 text-muted-foreground hover:text-accent"
          title="Open in HubSpot"
          aria-label="Open in HubSpot"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </td>
    </tr>
  );
}

// ============================================================================
// Tasks tab (legacy "Onboarding")
// ============================================================================

function TasksTab({ partnerId }: { partnerId: string }) {
  const query = useOnboardingTasks(partnerId);
  const toggle = useToggleOnboardingTask();
  const remove = useDeleteOnboardingTask();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Onboarding tasks. Partner-assigned tasks appear on the portal
          onboarding page.
        </p>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add task
        </Button>
      </div>

      <AddTaskDialog
        partnerId={partnerId}
        open={showAdd}
        onClose={() => setShowAdd(false)}
      />

      {query.isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : query.isError ? (
        <EmptyState
          icon={Calendar}
          title="Couldn't load tasks"
          description={query.error?.message ?? "Try refreshing."}
        />
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No tasks yet"
          description="Add an onboarding step the partner needs to complete."
        />
      ) : (
        <ul className="divide-y divide-border/70 rounded-xl border bg-card">
          {(query.data ?? []).map((t) => {
            const done = t.status === "completed";
            return (
              <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                <input
                  type="checkbox"
                  checked={done}
                  onChange={(e) =>
                    toggle.mutate({
                      partnerId,
                      taskId: t.id,
                      done: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-border accent-accent"
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "text-sm font-medium",
                      done && "text-muted-foreground line-through",
                    )}
                  >
                    {t.title}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t.assigned_to_partner ? "Partner" : "Internal"}
                    {t.due_date ? ` · Due ${formatDate(t.due_date)}` : ""}
                    {t.description ? ` · ${t.description}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove.mutate({ partnerId, taskId: t.id })}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Delete task"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AddTaskDialog({
  partnerId,
  open,
  onClose,
}: {
  partnerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const add = useAddOnboardingTask();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const title = String(fd.get("title") ?? "").trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    const assignedToPartner = fd.get("assigned_to_partner") === "on";
    // Phase 6 partnerOnboardingTaskCreateSchema (packages/shared/schemas/
    // onboarding.ts) intentionally narrows to {title, assigned_to_partner}.
    // Description / due_date / status edits live elsewhere; a follow-up
    // edit-task UI lands when the broader admin task editor is ported.
    await add.mutateAsync({
      partnerId,
      title,
      assigned_to_partner: assignedToPartner,
    });
    toast.success("Task added");
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New onboarding task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-xs">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input id="title" name="title" required autoFocus />
          </div>
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              name="assigned_to_partner"
              className="h-4 w-4 rounded border-border accent-accent"
            />
            Visible to partner
          </label>
          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={onClose}
              disabled={add.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={add.isPending}>
              {add.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Notes tab
// ============================================================================

function NotesTab({ partnerId, initial }: { partnerId: string; initial: string }) {
  const [value, setValue] = useState(initial);
  const save = useUpdatePartnerNotes();
  const dirty = value !== initial;

  async function handleSave() {
    await save.mutateAsync({ id: partnerId, notes: value });
    toast.success("Notes saved");
  }

  return (
    <div className="rounded-xl border bg-card p-5">
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={10}
        placeholder="Internal notes, context, follow-ups…"
        maxLength={10_000}
      />
      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {value.length.toLocaleString()} / 10,000 characters
        </p>
        <Button onClick={handleSave} disabled={!dirty || save.isPending}>
          {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save notes
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Invite dialog
// ============================================================================

function InvitePartnerDialog({
  partnerId,
  open,
  onOpenChange,
}: {
  partnerId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const invite = useInvitePartnerContact();
  const [result, setResult] = useState<{ url: string } | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get("name") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    if (!name || !email) {
      toast.error("Name and email are required");
      return;
    }
    const response = await invite.mutateAsync({
      partnerId,
      name,
      email,
      roleAtPartner: String(fd.get("role_at_partner") ?? "").trim(),
    });
    setResult({ url: response.inviteUrl });
    toast.success(`Invite sent to ${email}`);
  }

  function handleClose(next: boolean) {
    if (!next) setResult(null);
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a partner contact</DialogTitle>
          <DialogDescription>
            They&apos;ll receive an email with a one-time invite link.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Invite created. The link below also works manually:
            </p>
            <Input value={result.url} readOnly onFocus={(e) => e.currentTarget.select()} />
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input id="name" name="name" required autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input id="email" name="email" type="email" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role_at_partner" className="text-xs">
                Role at partner
              </Label>
              <Input
                id="role_at_partner"
                name="role_at_partner"
                placeholder="e.g. Head of Partnerships"
              />
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                type="button"
                onClick={() => handleClose(false)}
                disabled={invite.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Send invite
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Skeletons
// ============================================================================

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-2 h-7 w-1/2" />
        <Skeleton className="mt-2 h-5 w-64" />
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
    </div>
  );
}
